'use client'
import { Checkmark, ChevronDown, Close } from '@carbon/icons-react'
import { FieldContext } from 'lib/formContext'
import Image from 'next/image'
import { FocusEventHandler, type ReactNode, use, useId, useRef } from 'react'
import type { InputProps } from 'react-select'
import {
  type ClearIndicatorProps,
  components,
  type DropdownIndicatorProps,
  type MultiValueGenericProps,
  type MultiValueRemoveProps,
  type OnChangeValue,
  type OptionProps,
  type SelectInstance,
  type SingleValueProps,
} from 'react-select'
import { twMerge } from 'tailwind-merge'
import type { Suggestion } from '../../../lib/suggestions'
import Loading from '../Loading'
import { AsyncSelect as BaseSelect } from './ComboboxSelect'
import {
  ComboboxOption,
  comboboxClassNames,
  comboboxStyles,
  controlFocus,
  GRAY_HOVER,
  type SelectVariant,
  VALUE_IMAGE,
} from './comboboxCommon'
import { FieldBorder } from './FieldBorder'

interface Props<IsMulti extends boolean = false> {
  variant?: SelectVariant
  name?: string
  route?: string
  loadOptions?: (query: string) => Promise<Suggestion[]>
  defaultValue?: IsMulti extends true ? Suggestion[] : Suggestion
  placeholder?: {
    title: string
    subtitle?: string
    image?: ReactNode
  }
  value?: IsMulti extends true ? Suggestion[] : Suggestion | null
  onChange?: (value: OnChangeValue<Suggestion, IsMulti>) => void
  /**
   * pass 'true' to submit the current form when the user blurs the input
   * pass 'hasValue' to submit only if the input has a value
   */
  submitOnBlur?: boolean | 'hasValue'
  disabled?: boolean
  required?: boolean
  className?: string
  multiple?: IsMulti
  menuPlacement?: 'auto' | 'top' | 'bottom'
  ignore?: string[] | null
  blurOnChange?: boolean
  max?: number
  menuPosition?: 'fixed' | 'absolute'
  onFocus?: FocusEventHandler<HTMLInputElement>
  hideSubtitleInSingleValue?: boolean
}

/**
 * Another attempt to build a reusable combobox, this time based on react-select.
 * Can be used in two ways:
 * 1. Provide a `route` prop pointing to an API endpoint that returns suggestions based on a query parameter (q)
 * 2. Provide a `loadOptions` function for custom fetching
 *
 * The placeholder is important, not only to show intent but also so that the combobox height does not jump.
 * If the suggestions have images, so should the placeholder.
 *
 * See EmployeeInput for an example of how to use this component.
 */
export function Combobox<IsMulti extends boolean = false>({
  variant = 'regular',
  name,
  route,
  loadOptions: customLoadOptions,
  defaultValue,
  value,
  onChange,
  submitOnBlur,
  placeholder,
  disabled,
  required,
  className,
  multiple,
  menuPlacement,
  ignore,
  blurOnChange,
  max,
  menuPosition,
  onFocus,
  hideSubtitleInSingleValue = false,
}: Props<IsMulti>) {
  const context = use(FieldContext)
  const ref = useRef<SelectInstance<Suggestion, IsMulti>>(null)
  const id = useId()
  const styles = comboboxStyles<Suggestion, IsMulti>({
    reserveMenuHeight: true,
    variant,
  })

  const loadOptions = customLoadOptions
    ? async (query: string) => {
        const data = await customLoadOptions(query)
        if (ignore) {
          return data.filter((item) => !ignore.includes(item.id))
        }
        return data
      }
    : route
      ? async (query: string) => {
          const [pathname, search] = route.split('?')
          const searchParams = new URLSearchParams(search)
          searchParams.set('q', query)
          if (ignore) {
            ignore.forEach((id) => searchParams.append('ignore[]', id))
          }
          const res = await fetch(`${pathname}?${searchParams}`)
          const data: Suggestion[] = await res.json()
          if (ignore) {
            return data.filter((item) => !ignore.includes(item.id))
          }
          return data
        }
      : async () => {
          throw new Error(
            'Combobox requires either a `route` or `loadOptions` prop',
          )
        }

  return (
    <FieldBorder className={variant === 'slim' ? 'min-h-8' : undefined}>
      <BaseSelect<Suggestion, IsMulti>
        variant={variant}
        instanceId={id}
        ref={ref}
        id={context?.id}
        name={name ?? context?.name}
        className={className}
        aria-invalid={context?.error}
        aria-errormessage={context?.error ? `${context.id}-helper` : undefined}
        loadOptions={loadOptions}
        isOptionDisabled={(_, selectValue) =>
          multiple && max && selectValue.length >= max ? true : false
        }
        defaultOptions
        defaultValue={defaultValue}
        value={value}
        isDisabled={disabled ?? context?.disabled}
        required={required}
        placeholder={
          placeholder ? (
            <div className="flex items-center gap-2 py-1 pl-2">
              {placeholder.image && (
                <div
                  className={twMerge(
                    `${VALUE_IMAGE} relative my-1 h-6 w-6 shrink-0 overflow-hidden rounded-full`,
                    (disabled ?? context?.disabled) && 'opacity-50',
                  )}
                >
                  {placeholder.image}
                </div>
              )}
              <div className="flex min-w-0 flex-1 flex-col gap-0">
                <span
                  className={twMerge(
                    'body-compact-01 truncate',
                    (disabled ?? context?.disabled)
                      ? 'text-gray-disabled'
                      : 'text-text-placeholder',
                  )}
                >
                  {placeholder.title}
                </span>
                {placeholder.subtitle && (
                  <span className="select-value-subtitle label-00 text-text-secondary truncate">
                    {placeholder.subtitle}
                  </span>
                )}
              </div>
            </div>
          ) : (
            'Auswählen'
          )
        }
        classNames={comboboxClassNames<Suggestion, IsMulti>(variant)}
        styles={{
          ...styles,
          multiValue: (base, props) =>
            props.data.imageUrl || props.data.renderIcon
              ? {
                  ...base,
                  position: 'relative',
                  marginRight: '-0.75rem',
                  borderRadius: '9999px',
                  overflow: 'hidden',
                  width: variant === 'slim' ? '1.25rem' : '1.5rem',
                  height: variant === 'slim' ? '1.25rem' : '1.5rem',
                  marginTop: '0.25rem',
                  marginBottom: '0.25rem',
                }
              : (styles.multiValue?.(base, props) ?? base),
        }}
        classNamePrefix={'react-select'}
        components={
          hideSubtitleInSingleValue
            ? comboboxComponentsHideSubtitleInSingleValue
            : comboboxComponents
        }
        getOptionValue={(option) => option.id}
        isClearable={!required}
        hideSelectedOptions={false}
        closeMenuOnSelect={!multiple}
        loadingMessage={() => (
          <Loading className="flex w-full justify-center py-3" />
        )}
        noOptionsMessage={() => 'Keine weiteren Optionen verfügbar'}
        onChange={(newValue) => {
          context?.onChange(
            Array.isArray(newValue)
              ? newValue.map((option: Suggestion) => option.id)
              : ((newValue as Suggestion | null)?.id ?? null),
          )
          if (blurOnChange) {
            setTimeout(() => {
              ref.current?.blur()
            })
          }
          onChange?.(newValue)
        }}
        onFocus={onFocus}
        onBlur={(event) => {
          if (
            submitOnBlur === true ||
            (submitOnBlur === 'hasValue' && ref.current?.hasValue())
          ) {
            event.currentTarget.form?.requestSubmit()
          }
        }}
        onKeyDown={(e) => controlFocus(ref.current?.inputRef, e)}
        isMulti={multiple}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            dangerLight: GRAY_HOVER,
          },
        })}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
      />
    </FieldBorder>
  )
}

function SingleValueWithSubtitle(props: SingleValueProps<Suggestion>) {
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2 py-1 pl-2">
        {props.data.renderIcon
          ? props.data.renderIcon({ data: props.data })
          : props.data.imageUrl && (
              <div
                className={`${VALUE_IMAGE} relative my-1 h-6 w-6 shrink-0 overflow-hidden rounded-full`}
              >
                <Image src={props.data.imageUrl} fill alt={props.data.title} />
              </div>
            )}
        <div className="flex min-w-0 flex-1 flex-col gap-0">
          <span
            className={twMerge(
              'body-compact-01 truncate',
              props.isDisabled ? 'text-gray-disabled' : 'text-text-primary',
            )}
          >
            {props.data.title}
          </span>
          {props.data.subtitle && (
            <span
              className={twMerge(
                'select-value-subtitle label-00 text-text-secondary truncate',
                props.isDisabled && 'text-gray-disabled',
              )}
            >
              {props.data.subtitle}
            </span>
          )}
        </div>
      </div>
    </components.SingleValue>
  )
}

function SingleValueTitleOnly(props: SingleValueProps<Suggestion>) {
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2 py-2 pl-2">
        {props.data.renderIcon
          ? props.data.renderIcon({ data: props.data })
          : props.data.imageUrl && (
              <div
                className={`${VALUE_IMAGE} relative my-1 h-6 w-6 shrink-0 overflow-hidden rounded-full`}
              >
                <Image src={props.data.imageUrl} fill alt={props.data.title} />
              </div>
            )}
        <span
          className={twMerge(
            'truncate',
            props.isDisabled ? 'text-gray-disabled' : 'text-text-primary',
          )}
        >
          {props.data.title}
        </span>
      </div>
    </components.SingleValue>
  )
}

const comboboxComponents = {
  DropdownIndicator(props: DropdownIndicatorProps<Suggestion>) {
    return (
      <components.DropdownIndicator {...props}>
        <ChevronDown
          className={
            props.isDisabled ? 'text-gray-disabled' : 'text-text-primary'
          }
        />
      </components.DropdownIndicator>
    )
  },
  Option(props: OptionProps<Suggestion>) {
    return (
      <ComboboxOption {...props}>
        <div
          className={twMerge(
            'flex items-center gap-4',
            props.isDisabled && 'opacity-10',
          )}
        >
          {props.data.renderIcon
            ? props.data.renderIcon({ data: props.data })
            : props.data.imageUrl && (
                <div className="relative h-6 w-6 shrink-0 overflow-hidden rounded-full">
                  <Image
                    src={props.data.imageUrl}
                    fill
                    alt={props.data.title}
                  />
                </div>
              )}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="truncate">{props.data.title}</span>
            {props.data.subtitle && (
              <span className="text-text-secondary truncate">
                {props.data.subtitle}
              </span>
            )}
          </div>
          {!props.isMulti && props.isSelected && (
            <Checkmark className="ml-auto" />
          )}
        </div>
      </ComboboxOption>
    )
  },
  SingleValue: SingleValueWithSubtitle,
  ClearIndicator(props: ClearIndicatorProps<Suggestion>) {
    return (
      <components.ClearIndicator {...props}>
        <Close className="text-text-primary" />
      </components.ClearIndicator>
    )
  },
  MultiValueLabel(props: MultiValueGenericProps<Suggestion>) {
    return (
      <components.MultiValueLabel {...props}>
        {props.data.renderIcon ? (
          props.data.renderIcon({ data: props.data })
        ) : props.data.imageUrl ? (
          <Image src={props.data.imageUrl} fill alt={props.data.title} />
        ) : null}
        {!props.data.renderIcon && !props.data.imageUrl && (
          <span>{props.data.title}</span>
        )}
      </components.MultiValueLabel>
    )
  },
  MultiValueRemove(props: MultiValueRemoveProps<Suggestion>) {
    return (
      <components.MultiValueRemove {...props}>
        <Close className="text-text-primary" />
      </components.MultiValueRemove>
    )
  },
  Input(props: InputProps<Suggestion>) {
    return <components.Input {...props} aria-activedescendant={undefined} />
  },
}

const comboboxComponentsHideSubtitleInSingleValue = {
  ...comboboxComponents,
  SingleValue: SingleValueTitleOnly,
}
